/* The complete Whisper, read at the Keeper's own pace.
 * The supplied manuscript transcription is the only source of words.
 * Titles, edition subtitles and repeated printer's wordmarks are omitted here.
 */
(() => {
  'use strict';

  const source = window.VIULET_PRINTED_PIECE_TEXT;
  if (!source?.['manuscript-front'] || !source?.['manuscript-back']) return;
  const furniture = new Set([
    'Letters To The Subconscious', '(The Remastered Edition)', 'Mirrors by Viulet'
  ]);
  const paragraphs = [...source['manuscript-front'], ...source['manuscript-back']]
    .filter(text => !furniture.has(text));
  const wordCount = text => text.trim().split(/\s+/u).length;

  // Keep a short introduction and its answer together at a page turn.
  const blocks = [];
  for (let index = 0; index < paragraphs.length; index += 1) {
    const block = [paragraphs[index]];
    if (paragraphs[index].endsWith(':') && paragraphs[index + 1]) {
      block.push(paragraphs[++index]);
      if (wordCount(paragraphs[index]) <= 12 && paragraphs[index + 1]) {
        block.push(paragraphs[++index]);
      }
    }
    blocks.push(block);
  }
  const pages = [];
  let page = [], words = 0;
  blocks.forEach(block => {
    const count = block.reduce((total, text) => total + wordCount(text), 0);
    if (page.length && words + count > 85) {
      pages.push(Object.freeze(page)); page = []; words = 0;
    }
    page.push(...block); words += count;
  });
  if (page.length) pages.push(Object.freeze(page));
  window.VIULET_WHISPER_CONTENT = Object.freeze({
    paragraphs: Object.freeze(paragraphs), pages: Object.freeze(pages),
    wordCount: paragraphs.reduce((total, text) => total + wordCount(text), 0)
  });
  if (typeof document === 'undefined') return;

  const $ = selector => document.getElementById('viulet-v36').querySelector(selector);
  const toggle = $('#whisper-toggle'), panel = $('#full-whisper');
  const book = $('#whisper-pages'), position = $('#whisper-position');
  const previous = $('#whisper-previous'), next = $('#whisper-next');
  const progress = $('.reading-progress i');
  if (!toggle || !panel || !book || !position || !previous || !next) return;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const ease = 'cubic-bezier(.22,.61,.36,1)';
  let current = 0, requested = 0, revision = 0, animation = null, sizeFrame = 0;
  let expanded = false, measuredWidth = 0;

  // Emphasis changes the reading rhythm, never the manuscript's wording.
  const emphases = [
    ['You are not broken. You are not behind. You are not too late.', 'strong'],
    ['But today, something stirs.', 'em'],
    ['In your hand is a brush, shaking, but still yours.', 'strong'],
    ['The world outside mirrors the world within.', 'em'],
    ['But you’re allowed to change the script.', 'strong'],
    ['the sunlight after it.', 'em'],
    ['Bold, beautiful, whole.', 'strong'],
    ['the cage dissolves.', 'em'],
    ['You were never the storm. You were always the artist. You were always the light.', 'strong'],
    ['Welcome home. It is good to have you back.', 'em']
  ];
  function makeParagraph(text, wordOffset) {
    const paragraph = document.createElement('p');
    paragraph.className = 'whisper-passage';
    let cursor = 0;
    const matches = emphases.map(([phrase, tag]) => ({phrase, tag, start: text.indexOf(phrase)}))
      .filter(match => match.start >= 0).sort((a, b) => a.start - b.start);
    matches.forEach(match => {
      if (match.start < cursor) return;
      paragraph.append(text.slice(cursor, match.start));
      const accent = document.createElement(match.tag);
      accent.textContent = match.phrase; paragraph.append(accent);
      cursor = match.start + match.phrase.length;
    });
    paragraph.append(text.slice(cursor));
    const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT), nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let offset = wordOffset;
    nodes.forEach(node => {
      const fragment = document.createDocumentFragment();
      node.textContent.split(/(\s+)/u).forEach(token => {
        if (!token.trim()) fragment.append(token);
        else {
          const span = document.createElement('span');
          span.className = 'reading-word'; span.textContent = token;
          span.style.setProperty('--word-delay', `${Math.min(offset++ * 27, 2300)}ms`);
          fragment.append(span);
        }
      });
      node.replaceWith(fragment);
    });
    return paragraph;
  }

  const leaves = pages.map((texts, index) => {
    const leaf = document.createElement('div');
    leaf.className = 'whisper-leaf'; leaf.hidden = index !== current;
    let offset = 0;
    texts.forEach(text => {
      leaf.append(makeParagraph(text, offset)); offset += wordCount(text);
    });
    return leaf;
  });
  book.replaceChildren(...leaves);
  panel.classList.add('whisper-reader-v34');
  book.tabIndex = 0;
  book.setAttribute('role', 'region');
  position.setAttribute('aria-live', 'polite');
  position.setAttribute('aria-atomic', 'true');
  previous.type = 'button'; next.type = 'button'; toggle.type = 'button';

  function updateControls() {
    position.textContent = `${String(current + 1).padStart(2, '0')} / ${String(pages.length).padStart(2, '0')}`;
    book.setAttribute('aria-label', `The complete Whisper, page ${current + 1} of ${pages.length}`);
    previous.disabled = current === 0;
    previous.setAttribute('aria-label', 'Previous page of the Whisper');
    next.replaceChildren(current === pages.length - 1 ? 'Read the manuscript ' : 'Continue ');
    const arrow = document.createElement('span');
    arrow.textContent = '→';
    arrow.setAttribute('aria-hidden', 'true'); next.append(arrow);
    next.setAttribute('aria-label', current === pages.length - 1 ? 'Read the original manuscript' : 'Next page of the Whisper');
    if (progress) progress.style.width = `${(current + 1) / pages.length * 100}%`;
  }

  function cancelTurn() {
    revision += 1; animation?.cancel(); animation = null;
    requested = current;
    book.removeAttribute('aria-busy');
  }

  function measureReadingHeight() {
    sizeFrame = 0;
    const style = getComputedStyle(book);
    const width = book.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0);
    if (width <= 0) return;
    let tallest = 0;
    leaves.forEach(leaf => {
      const measure = leaf.cloneNode(true);
      measure.hidden = false; measure.classList.remove('is-reading');
      measure.classList.add('whisper-measure');
      measure.setAttribute('aria-hidden', 'true'); measure.style.width = `${width}px`;
      book.append(measure);
      tallest = Math.max(tallest, measure.getBoundingClientRect().height);
      measure.remove();
    });
    // Reserve the same area on every page; small phones can scroll the prose.
    const allowance = Math.max(225, Math.min(590, innerHeight - 245));
    const height = Math.min(allowance, Math.max(260, Math.ceil(tallest + 4)));
    book.style.setProperty('--whisper-reading-height', `${height}px`);
    measuredWidth = book.clientWidth;
  }
  function scheduleMeasure() {
    if (!sizeFrame) sizeFrame = requestAnimationFrame(measureReadingHeight);
  }

  function beginReading(leaf, stamp) {
    leaf.classList.remove('is-reading');
    requestAnimationFrame(() => {
      if (stamp === revision && expanded && !reduced.matches) leaf.classList.add('is-reading');
    });
  }

  function keepReadingInView() {
    const reading = panel.querySelector('.whisper-reading');
    const bounds = reading.getBoundingClientRect();
    if (bounds.top >= 18 && bounds.bottom <= innerHeight - 18) return;
    const top = Math.max(18, (innerHeight - bounds.height) / 2);
    scrollBy({top: bounds.top - top, behavior: reduced.matches ? 'instant' : 'smooth'});
  }

  async function turnPage(target) {
    target = Math.max(0, Math.min(pages.length - 1, target));
    if (!expanded || target === requested) return;
    requested = target;
    const stamp = ++revision;
    const opacity = getComputedStyle(book).opacity;
    animation?.cancel(); book.setAttribute('aria-busy', 'true');
    if (!reduced.matches) {
      animation = book.animate([{opacity}, {opacity: 0}], {duration: 260, easing: ease, fill: 'forwards'});
      await animation.finished.catch(() => {});
    }
    if (stamp !== revision || !expanded) return;
    current = target;
    leaves.forEach((leaf, index) => {
      leaf.hidden = index !== current; leaf.classList.remove('is-reading');
    });
    book.scrollTop = 0; updateControls();
    animation?.cancel(); animation = null;
    if (!reduced.matches) {
      animation = book.animate([{opacity: 0}, {opacity: 1}], {duration: 850, easing: ease});
    }
    beginReading(leaves[current], stamp);
    keepReadingInView();
    book.removeAttribute('aria-busy');
  }

  function setExpanded(open) {
    cancelTurn(); expanded = open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.firstElementChild.textContent = open ? 'Close the Whisper' : 'Read the full Whisper';
    panel.inert = !open; panel.setAttribute('aria-hidden', String(!open));
    panel.classList.toggle('is-open', open);
    leaves.forEach(leaf => leaf.classList.remove('is-reading'));
    if (open) {
      scheduleMeasure(); beginReading(leaves[current], revision);
      const stamp = revision;
      requestAnimationFrame(async () => {
        await Promise.all(panel.getAnimations().map(item => item.finished.catch(() => {})));
        if (expanded && stamp === revision) { measureReadingHeight(); keepReadingInView(); }
      });
    }
    updateControls();
    panel.dispatchEvent(new CustomEvent('viulet:whisper-state', {bubbles: true, detail: {open}}));
  }

  toggle.addEventListener('click', () => setExpanded(!expanded));
  previous.addEventListener('click', () => turnPage(requested - 1));
  next.addEventListener('click', () => {
    if (requested < pages.length - 1) turnPage(requested + 1);
    else if (current === pages.length - 1) {
      next.dispatchEvent(new CustomEvent('viulet:read-manuscript', {
        bubbles: true, detail: {paper: 'manuscript', launcher: next}
      }));
    }
  });
  book.addEventListener('keydown', event => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.key === 'ArrowRight' && requested < pages.length - 1) {
      event.preventDefault(); turnPage(requested + 1);
    } else if (event.key === 'ArrowLeft' && requested > 0) {
      event.preventDefault(); turnPage(requested - 1);
    }
  });
  panel.addEventListener('keydown', event => {
    if (event.key === 'Escape' && expanded) {
      event.preventDefault(); setExpanded(false); toggle.focus({preventScroll: true});
    }
  });
  addEventListener('resize', scheduleMeasure, {passive: true});
  if ('ResizeObserver' in window) {
    new ResizeObserver(() => {
      if (Math.abs(book.clientWidth - measuredWidth) > .5) scheduleMeasure();
    }).observe(book);
  }
  document.fonts?.ready.then(scheduleMeasure);
  reduced.addEventListener('change', () => {
    cancelTurn(); leaves.forEach(leaf => leaf.classList.remove('is-reading'));
    if (expanded && !reduced.matches) beginReading(leaves[current], revision);
  });
  setExpanded(false);
  scheduleMeasure();
})();
